import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import LoadingSpinner from '../components/LoadingSpinner';

const JoinOrganizationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [invite, setInvite] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);

  const inviteId = searchParams.get('inviteId');

  useEffect(() => {
    if (!inviteId) {
      setError('No invitation ID provided. Please use the link from your invitation email.');
      setLoading(false);
      return;
    }

    if (!currentUser) {
      // If user is not logged in, redirect them to the signup page,
      // passing the invite details in the state so we can process it after signup.
      navigate('/signup', { state: { from: `/join-organization?inviteId=${inviteId}` } });
      return;
    }

    const fetchInvite = async () => {
      try {
        const inviteRef = doc(db, 'organizationInvites', inviteId);
        const inviteSnap = await getDoc(inviteRef);

        if (!inviteSnap.exists()) {
          setError('This invitation is invalid or has expired.');
          return;
        }

        const inviteData = inviteSnap.data();

        if (inviteData.status !== 'pending') {
          setError(`This invitation has already been ${inviteData.status}.`);
          return;
        }
        
        // Ensure the invite is for the currently logged-in user
        if (inviteData.recipientEmail.toLowerCase() !== currentUser.email.toLowerCase()) {
          setError('This invitation is for a different user.');
          return;
        }

        setInvite({ id: inviteSnap.id, ...inviteData });

        // Fetch organization details
        const orgRef = doc(db, 'organizations', inviteData.organizationId);
        const orgSnap = await getDoc(orgRef);
        if (orgSnap.exists()) {
          setOrganization({ id: orgSnap.id, ...orgSnap.data() });
        } else {
          setError('The organization associated with this invite could not be found.');
        }

      } catch (err) {
        console.error("Error fetching invitation:", err);
        setError('An error occurred while fetching the invitation details.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [inviteId, currentUser, navigate]);

  const handleAcceptInvite = async () => {
    if (!invite || !organization || !currentUser) return;

    setIsAccepting(true);
    try {
      const orgId = organization.id;
      const userId = currentUser.uid;

      // 1. Add user to the organization's member list (source of truth for admin list, Subscription)
      await setDoc(doc(db, 'organizations', orgId, 'members', userId), {
        role: organization.settings?.defaultRole || 'Member',
        status: 'active',
        joinedAt: serverTimestamp(),
        name: currentUser.displayName || currentUser.email || 'Member',
        email: currentUser.email || '',
        appID: userId,
      });

      // 2. Add the org to the user's `activeOrgs` array
      await updateDoc(doc(db, 'users', userId), {
        activeOrgs: arrayUnion(orgId)
      });

      // 3. Update user's subcollection for this org
      await setDoc(doc(db, 'users', userId, 'organizations', orgId), {
        role: organization.settings?.defaultRole || 'Member',
        status: 'active',
        joinedAt: serverTimestamp()
      }, { merge: true });

      // 4. Update invite status
      await updateDoc(doc(db, 'organizationInvites', invite.id), {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedBy: userId
      });

      // 5. Redirect to the community page
      navigate('/my-community');

    } catch (err) {
      console.error("Error accepting invitation:", err);
      setError('An error occurred while trying to accept the invitation.');
      setIsAccepting(false);
    }
  };


  if (loading) {
    return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg">
        {error ? (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Error</h2>
            <p className="mt-2 text-gray-600">{error}</p>
          </div>
        ) : organization ? (
          <div className="text-center">
            <img src={organization.logoUrl} alt={`${organization.displayName} logo`} className="mx-auto h-24 w-24 object-contain rounded-lg shadow-sm mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">
              You're invited to join
            </h2>
            <p className="mt-2 text-4xl font-extrabold text-blue-600">
              {organization.displayName}
            </p>
            <div className="mt-8">
              <button
                onClick={handleAcceptInvite}
                disabled={isAccepting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
              >
                {isAccepting ? 'Accepting...' : 'Accept Invitation'}
              </button>
            </div>
          </div>
        ) : (
           <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-600">Invalid Invitation</h2>
            <p className="mt-2 text-gray-600">The link you used is either incorrect or has expired.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinOrganizationPage; 